/**
 * The promoter RPCs (db/migrations/0273, design §8.4), typed. Every call goes through PostgREST on the partners
 * host, where Caddy sets `X-Coreway-Surface: promoter`; off that host every one answers 42501. Errors are
 * thrown as PromoterRpcError (code + the server's English message, never rendered: promoterErrorKey maps it to
 * the `promoter.errors.*` copy).
 */
import type { SupabaseClient } from '@supabase/supabase-js';

export type GateState = 'not_invited' | 'claimable' | 'ok';
export type W9Status = 'none' | 'submitted' | 'accepted' | 'rejected';

export interface PromoterIdentity {
  id: string;
  code: string;
  display_name: string;
  status: 'active' | 'suspended' | 'closed';
  locale: string;
}

export interface WhoAmI {
  state: GateState;
  enabled: boolean;
  has_password: boolean;
  promoter: PromoterIdentity | null;
  terms_pending: boolean;
  w9_status: W9Status | null;
}

export interface TermsDocument {
  document_id: string;
  program_label: string;
  version: string;
  object_name: string;
  sha256: string;
  size_bytes: number;
  pdf_base64: string;
  signed: boolean;
  consent: { version: string; locale: string; body: string; sha256: string } | null;
}

export type AgencyState = 'not_paying' | 'in_bar' | 'bar_done' | 'paused_comp';
export type PayoutBlockedReason = 'w9_required' | 'request_pending' | 'nothing_available';

export interface DashboardAgency {
  key: string;
  name: string;
  time_zone: string | null;
  attached_at: string;
  source: 'link' | 'typed' | 'operator' | string;
  state: AgencyState;
  bar_starts_at: string | null;
  bar_base_ends_at: string | null;
  bar_ends_at: string | null;
  extension_days: number | null;
  filled_days: number | null;
  commission_days: number;
  earned_cents: number;
}

export interface DashboardCommission {
  agency_key: string;
  agency_name: string | null;
  time_zone: string | null;
  kind: 'commission' | 'clawback' | 'restore';
  adjusts_paid_at: string | null;
  invoice_paid_at: string;
  net_base_cents: number;
  reversed_cents: number;
  covered_days: number;
  filled_days: number;
  amount_cents: number;
  state: 'held' | 'available' | 'paid' | 'reversed';
  payable_at: string;
}

export interface Dashboard {
  promoter: PromoterIdentity & { status_reason: string | null; signed_at: string | null };
  program: {
    label: string;
    commission_percent: number;
    commission_days: number;
    payout_hold_days: number;
    attach_starts_at: string | null;
    attach_ends_at: string | null;
    active: boolean;
  };
  share: { code: string; signup_url: string };
  balance: { available_cents: number; held_cents: number; paid_cents: number };
  agencies: DashboardAgency[];
  commissions: DashboardCommission[];
  payouts: { amount_cents: number; method: string; reference: string | null; paid_on: string; recorded_at: string }[];
  requests: {
    requested_at: string;
    available_cents_at_request: number;
    status: 'pending' | 'paid' | 'rejected';
    decided_at: string | null;
    reject_reason: string | null;
  }[];
  w9: { status: W9Status; submitted_at: string | null; accepted_at: string | null; reject_reason: string | null };
  signed_documents: { document_id: string; version: string; sha256: string; signed_at: string }[];
  can_request_payout: boolean;
  payout_blocked_reason: PayoutBlockedReason | null;
}

export class PromoterRpcError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'PromoterRpcError';
    this.code = code;
  }
}

async function call<T>(client: SupabaseClient, name: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = args === undefined ? await client.rpc(name) : await client.rpc(name, args);
  if (error) throw new PromoterRpcError(error.code ?? '', error.message ?? '');
  return data as T;
}

export const whoami = (client: SupabaseClient) => call<WhoAmI>(client, 'promoter_whoami');
export const claim = (client: SupabaseClient) => call<WhoAmI>(client, 'promoter_claim');
/** 0276: records that THIS account set a password through the portal (GoTrue hides it: emailed-code accounts carry a random hash). */
export const passwordSet = (client: SupabaseClient) => call<WhoAmI>(client, 'promoter_password_set');
/** null = the program's current agreement; an id = a document this promoter signed. */
export const terms = (client: SupabaseClient, documentId: string | null = null) =>
  call<TermsDocument>(client, 'promoter_terms', { p_document: documentId });
export const dashboard = (client: SupabaseClient) => call<Dashboard>(client, 'promoter_dashboard');
export const payoutRequest = (client: SupabaseClient) => call<string>(client, 'promoter_payout_request');
export const w9Submit = (client: SupabaseClient, objectName: string) =>
  call<string>(client, 'promoter_w9_submit', { p_object_name: objectName });

export interface TermsSignInput {
  documentId: string;
  /** The SHA-256 THIS device computed over the PDF bytes it showed. */
  documentSha256: string;
  consentVersion: string;
  consentLocale: string;
  /** `data:image/png;base64,...` of the drawn signature. */
  signaturePng: string;
}

export const termsSign = (client: SupabaseClient, input: TermsSignInput) =>
  call<{ signature_id: string; signed_at: string }>(client, 'promoter_terms_sign', {
    p_document: input.documentId,
    p_document_sha256: input.documentSha256,
    p_consent_version: input.consentVersion,
    p_consent_locale: input.consentLocale,
    p_consent_given: true,
    p_signature_png: input.signaturePng,
  });

export const W9_BUCKET = 'referral-w9';
export const W9_MAX_BYTES = 10 * 1024 * 1024;

/**
 * Uploads a W-9 PDF to the private bucket at `<promoter id>/<uuid>.pdf` (the one shape its INSERT policy and
 * promoter_w9_submit accept), never overwriting, and returns that object name for promoter_w9_submit.
 */
export async function uploadW9(
  client: SupabaseClient,
  promoterId: string,
  file: File,
  newId: () => string = () => crypto.randomUUID(),
): Promise<string> {
  const path = `${promoterId}/${newId()}.pdf`;
  const { error } = await client.storage.from(W9_BUCKET).upload(path, file, { contentType: 'application/pdf', upsert: false });
  if (error) throw new PromoterRpcError(String((error as { statusCode?: string | number }).statusCode ?? 'storage'), error.message);
  return path;
}

export type PromoterErrorKey =
  | 'programClosed'
  | 'noInvitation'
  | 'alreadyClaimed'
  | 'agreementChanged'
  | 'consentChanged'
  | 'consentRequired'
  | 'signatureRequired'
  | 'signatureTooLarge'
  | 'agreementRequired'
  | 'w9Required'
  | 'requestPending'
  | 'nothingAvailable'
  | 'uploadNotFound'
  | 'notPdf'
  | 'tooLarge'
  | 'alreadySubmitted'
  | 'notAllowed'
  | 'network'
  | 'generic';

/** The server's messages (0273), matched as written there. */
const MESSAGES: [RegExp, PromoterErrorKey][] = [
  [/referral program is not open/, 'programClosed'],
  [/no invitation for this email/, 'noInvitation'],
  [/already belongs to a promoter|invitation was already claimed/, 'alreadyClaimed'],
  [/the agreement changed/, 'agreementChanged'],
  [/the consent text changed/, 'consentChanged'],
  [/consent is required/, 'consentRequired'],
  [/signature is empty|signature is not a PNG/, 'signatureRequired'],
  [/signature is too large/, 'signatureTooLarge'],
  [/agreement signature required/, 'agreementRequired'],
  [/accepted W-9 is required/, 'w9Required'],
  [/payout request is already pending/, 'requestPending'],
  [/nothing available to pay/, 'nothingAvailable'],
  [/upload not found/, 'uploadNotFound'],
  [/is not a PDF/, 'notPdf'],
  [/larger than 10 MB/, 'tooLarge'],
  [/already submitted/, 'alreadySubmitted'],
];

export function promoterErrorKey(err: unknown): PromoterErrorKey {
  const e = (err ?? {}) as { code?: string; message?: string };
  const message = e.message ?? '';
  for (const [re, key] of MESSAGES) if (re.test(message)) return key;
  if (e.code === '42501') return 'notAllowed';
  if (/Failed to fetch|NetworkError|Load failed|fetch failed/i.test(message)) return 'network';
  return 'generic';
}
